/**
 * Product categories, as rows an admin owns.
 *
 * These used to be two hardcoded arrays in this codebase and two Prisma enums in the
 * backend, which meant adding "Grooming tools" to the shop was a schema migration, a
 * deploy, and a matching edit here. Every screen that offered a category now reads this.
 */
export type Vertical = 'FOOD' | 'PHARMACY';

export type ProductCategory = {
  id: string;
  vertical: Vertical;
  /**
   * What goes into every product row. Immutable once created — renaming it would orphan
   * every product carrying the old value, so the console never offers to change it.
   */
  key: string;
  /** What a human reads. Editing this renames the category everywhere at once. */
  label: string;
  /**
   * API path to the category artwork, or null when nobody has uploaded one.
   *
   * Null on the twelve seeded categories, which predate icons — not an error. The shop draws a
   * placeholder and the console asks an admin to fill it in.
   */
  icon_path: string | null;
  display_order: number;
  is_active: boolean;
  updated_at: string;
};

/** The admin list carries what is behind each category, which is what makes it decidable. */
export type CategoryWithUsage = ProductCategory & {
  /** Live and pending vendor listings filed under it. */
  listings: number;
  /** Curated catalogue masters filed under it. */
  masters: number;
};

export type CreateCategoryInput = {
  vertical: Vertical;
  label: string;
  key?: string;
  icon_s3_key?: string | null;
};

export type UpdateCategoryInput = {
  label?: string;
  display_order?: number;
  is_active?: boolean;
  /** Null clears the icon; omitted leaves it alone. */
  icon_s3_key?: string | null;
};

export const VERTICAL_LABEL: Record<Vertical, string> = {
  FOOD: 'Food & products',
  PHARMACY: 'Medicines',
};

/**
 * The machine key the backend will derive from a label, mirrored so the console can show
 * it before saving.
 *
 * Shown rather than asked for: the key is an implementation detail an admin should not
 * have to invent, and one they cannot change afterwards — so asking them to type it would
 * be a trap rather than a choice. Seeing what it will be is still worth something, because
 * it is what appears in exports and in the API.
 */
export function key_from_label(label: string): string {
  return label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

/** Total of everything filed under a category — what makes it un-deletable. */
export function usage_total(c: CategoryWithUsage): number {
  return c.listings + c.masters;
}

/**
 * How a category's reach reads in a sentence.
 *
 * Spelled out rather than left as a number beside a delete button, because "37" does not
 * say "thirty-seven things stop being findable if you get this wrong".
 */
export function usage_summary(c: CategoryWithUsage): string {
  const parts: string[] = [];
  if (c.listings) parts.push(`${c.listings} listing${c.listings === 1 ? '' : 's'}`);
  if (c.masters) parts.push(`${c.masters} catalogue product${c.masters === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' · ') : 'Nothing filed under it yet';
}

/**
 * Pick a category's label out of a list.
 *
 * Falls back to the key rather than rendering nothing: a product filed under a category
 * that has since been deleted should still show what it says, not a blank cell.
 */
export function category_label(
  categories: ProductCategory[] | undefined,
  vertical: Vertical | undefined,
  key: string,
): string {
  const found = categories?.find(
    (c) => c.key === key && (vertical === undefined || c.vertical === vertical),
  );
  return found?.label ?? key;
}

/**
 * Options for a filter that spans both shops.
 *
 * Deduplicated by key, because FOOD and PHARMACY each have a SUPPLEMENT and they are two
 * different categories — but a single "Category" dropdown filters on the key, so offering
 * "Supplements" twice would be two identical-looking options doing the same thing.
 */
export function shared_category_options(
  categories: ProductCategory[] | undefined,
): { value: string; label: string }[] {
  const seen = new Map<string, { label: string; live: boolean }>();
  for (const c of categories ?? []) {
    const prior = seen.get(c.key);
    if (!prior) {
      seen.set(c.key, { label: c.label, live: c.is_active });
    } else if (c.is_active) {
      // Live in either shop is live for a filter that spans both.
      seen.set(c.key, { label: prior.label, live: true });
    }
  }
  /*
   * Retired categories stay in FILTERS, marked. They still hold products — that is why
   * they were retired rather than deleted — and finding those products is exactly what
   * somebody needs to do next, usually to move them somewhere else. The product FORM is
   * the surface that must refuse them, and it does.
   */
  return [...seen].map(([value, { label, live }]) => ({
    value,
    label: live ? label : `${label} (retired)`,
  }));
}
